import { memo, useEffect, useMemo, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import { WwLoaderFill } from '../../shared/components/WwLoader'
import { previewOf, type ComposeRecipe, type Rect } from './pictureOps'
import { BrushControls, Window, useBrush } from './brushStudio'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * Edit area (Huanrui, 2026-10-06): change one part of a picture.
 *
 * TWO WINDOWS OF THE FULL-SIZE PICTURE, EACH A FIXED SIZE. Wire a picture in.
 * The upper window shows all of it; a box is dragged over what should
 * change. The words go in the box below, and Run sits between them and the
 * lower window. The lower window shows the part round the box, enlarged --
 * the original until a run, then the result. While a run is on its way the
 * loader is in this lower window. Nothing goes on the board until Apply:
 * then the picture appears to the right of the card, with the loader on it
 * while the full-size result is put together (Huanrui, 2026-10-06). The wheel over either window zooms the board, as anywhere else
 * on it (Huanrui, 2026-10-06).
 *
 * TWO BRUSHES. The result starts as the box with a soft edge. Eraser takes
 * the model's drawing away where the original should show -- which is also
 * how the join is softened -- and Restore paints it back (Huanrui,
 * 2026-10-06). Both are soft and round; their size and flow are the two
 * sliders, and moving either shows the brush in the middle of the window
 * for a moment. Apply puts the result in place of the picture the run made,
 * at full size; nothing is sent to the model again. Colours are matched to
 * the original on their own.
 *
 * On the board only for now: not on the overview, the skills page or Miro.
 */

/**
 * What the card keeps after a run, saved with the board: the model's own
 * picture and how it was made; once applied, the picture on the board it went
 * into and the painted mask. Nothing goes on the board before Apply.
 */
export interface EditBlend {
  frame?: string
  drawn: { store: 'library' | 'project'; id: string }
  recipe: ComposeRecipe
  made?: { prompt: string; model: string; size: string; quality: string; at: string; credits?: number }
  mask?: { store: 'library' | 'project'; id: string }
}

/**
 * What the windows draw from, held by the board in memory (ProjectCanvas,
 * EDIT AREA STUDIOS): the turned full-size picture, and after a run the
 * fitted patch with its mask. The mask is painted here, in place.
 */
export interface EditStudio {
  source: HTMLCanvasElement | null
  patch: { layer: HTMLCanvasElement; mask: HTMLCanvasElement; place: Rect } | null
  version: number
}

export interface EditNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  prompt?: string
  /** The box, as shares (0 to 1) of the picture as it is shown. Saved with the board. */
  box?: Rect
  runs?: string[]
  error?: string
  tried?: number
  credits?: number
  madeCount?: number
  blend?: EditBlend
  /** Putting the full-size result together. Its loader is on the picture, not here. */
  applying?: boolean
  /** Reading the picture and sending it up, before the run is on the queue. */
  preparing?: boolean
  /** The run being waited on, saved so a reload carries on waiting (ProjectCanvas, finishEdit). */
  pending?: { runId: string; recipe: ComposeRecipe; made: { prompt: string; model: string; size: string; quality: string; at: string } }


  /* handed in by the board, never stored */
  /** The picture wired in: its drawing copy, how it is turned, its shape as shown. */
  picture?: { url: string; turn: number; aspect: number; key: string } | null
  wired?: number
  /** How many reference pictures are wired into the numbered ports. */
  refs?: number
  estimate?: number
  live?: number
  maxLive?: number
  studio?: EditStudio | null
  onEdit?: (patch: Partial<EditNodeData>) => void
  onRun?: () => void
  onApply?: () => void
}

/* ------------------------------------------------------------------ */

/**
 * Port 1 (`in`) is the picture being edited. Ports 2, 3 ... (`ref-1`,
 * `ref-2` ...) are more pictures for the model to look at -- a label to copy,
 * a product to put in. There is always one empty port to wire into, up to
 * MAX_REFS references.
 */
const MAX_REFS = 5
function refPorts(refs: number): number {
  return Math.min(Math.max(2, refs + 1), MAX_REFS)
}

export const EditNode = memo(function EditNode({ id, data, selected }: NodeProps) {
  const card = data as EditNodeData
  const isOpen = card.open !== false
  const remeasure = useUpdateNodeInternals()
  const extra = refPorts(card.refs ?? 0)
  // A port that appears moves every other one; React Flow measures them once.
  useEffect(() => {
    remeasure(id)
  }, [id, extra, isOpen, remeasure])
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)
  const edit = (patch: Partial<EditNodeData>) => card.onEdit?.(patch)
  const picture = card.picture ?? null
  const studio = card.studio ?? null
  const source = studio?.source ?? null
  const patch = studio?.patch ?? null

  /*
   * The upper window from the board's own copy of the picture (Huanrui,
   * 2026-10-26): there at once, without waiting for the full-size picture,
   * which is read in the background (ProjectCanvas, sourceOf) for the lower
   * window, the run and Apply. The box is kept as shares of the picture, so
   * it is the same box on the full-size one. Until the full-size picture is
   * read the lower window shows this copy too, enlarged.
   */
  const previewKey = picture ? `${picture.url}|${picture.turn}` : ''
  const [preview, setPreview] = useState<{ key: string; canvas: HTMLCanvasElement } | null>(null)
  useEffect(() => {
    if (!picture || !isOpen || preview?.key === previewKey) return
    let gone = false
    previewOf(picture.url, picture.turn)
      .then((canvas) => {
        if (!gone) setPreview({ key: previewKey, canvas })
      })
      .catch((error: unknown) => console.warn('[WonderWorks] Edit area: the picture could not be shown', error))
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, isOpen])
  const copy = preview?.key === previewKey ? preview.canvas : null
  /** What the upper window draws: the copy, or the full-size picture if that came first. */
  const top = copy ?? source
  /** What the lower window draws: the full-size picture; the copy, enlarged, until then (not over a result). */
  const lower = source ?? (patch ? null : copy)
  /**
   * A run on its way, or its result still being fitted for the window: the
   * loader is in the lower window (Huanrui, 2026-10-06).
   */
  // Only a result for the picture wired in now counts: wiring in another
  // picture is not a run starting (Huanrui, 2026-10-06).
  const blendKey = card.blend
    ? `${card.blend.recipe.source.store}:${card.blend.recipe.source.id}:${card.blend.recipe.turn}`
    : ''
  const resultFor = Boolean(picture && blendKey === picture.key)
  const running = mine > 0 || card.preparing === true || Boolean(card.pending) || (resultFor && !patch)
  /** A run, or Apply (whose loader is on the picture to the right): the brushes wait. */
  const busy = running || card.applying === true
  const box = card.box
  const hasBox = Boolean(box && box.w > 0.01 && box.h > 0.01)
  const prompt = card.prompt ?? ''

  const [starting, setStarting] = useState(false)
  useEffect(() => {
    setStarting(false)
  }, [mine, card.error, card.tried])
  useEffect(() => {
    if (!starting) return
    // Longer than a picture card: the full-size picture is read and the part
    // round the box sent up before the run is even written down.
    const stop = setTimeout(() => setStarting(false), 60000)
    return () => clearTimeout(stop)
  }, [starting])

  /* ---------- the box, in the upper window ---------- */
  const [draft, setDraft] = useState<Rect | null>(null)
  const shown = draft ?? box ?? null
  const startBox = (at: { x: number; y: number }) => {
    if (!top) return null
    const pw = top.width
    const ph = top.height
    const from = { x: Math.min(1, Math.max(0, at.x / pw)), y: Math.min(1, Math.max(0, at.y / ph)) }
    let last: Rect = { ...from, w: 0, h: 0 }
    setDraft(last)
    return {
      move: (to: { x: number; y: number }) => {
        const x = Math.min(1, Math.max(0, to.x / pw))
        const y = Math.min(1, Math.max(0, to.y / ph))
        last = { x: Math.min(from.x, x), y: Math.min(from.y, y), w: Math.abs(x - from.x), h: Math.abs(y - from.y) }
        setDraft(last)
      },
      up: () => {
        // A click without a drag leaves the box that was there.
        if (last.w > 0.005 && last.h > 0.005) edit({ box: last })
        setDraft(null)
      },
    }
  }
  const drawBox = (ctx: CanvasRenderingContext2D, scale: number) => {
    if (!top || !shown || shown.w <= 0 || shown.h <= 0) return
    const r = { x: shown.x * top.width, y: shown.y * top.height, w: shown.w * top.width, h: shown.h * top.height }
    ctx.save()
    // Darken what is outside the box.
    ctx.fillStyle = 'rgba(0,0,0,0.38)'
    ctx.beginPath()
    ctx.rect(0, 0, top.width, top.height)
    ctx.rect(r.x, r.y, r.w, r.h)
    ctx.fill('evenodd')
    ctx.lineWidth = 1.5 / scale
    ctx.strokeStyle = '#ffffff'
    ctx.strokeRect(r.x, r.y, r.w, r.h)
    ctx.restore()
  }

  /* ---------- the two brushes, in the lower window (brushStudio.tsx) ---------- */
  const patches = useMemo(() => (patch ? [patch] : []), [patch])
  const brush = useBrush(patches, studio?.version)
  // The box that was drawn in the upper window, in the same place here
  // (Huanrui, 2026-10-06: the outline used to be the larger area the model
  // was sent, which did not line up with the box).
  const drawnBox = card.blend?.recipe.box
  const drawResult = (ctx: CanvasRenderingContext2D, scale: number) =>
    brush.render(ctx, scale, drawnBox ? [drawnBox] : [])

  /** The lower window: the part round the box, enlarged, with room round it. */
  const near: Rect | null = (() => {
    if (!lower) return null
    const b =
      patch && source
        ? card.blend?.recipe.box
        : box
          ? { x: box.x * lower.width, y: box.y * lower.height, w: box.w * lower.width, h: box.h * lower.height }
          : null
    if (!b || b.w <= 0 || b.h <= 0) return null
    const m = Math.max(b.w, b.h) * 0.3
    return { x: b.x - m, y: b.y - m, w: b.w + 2 * m, h: b.h + 2 * m }
  })()

  const why = !picture
    ? 'Connect a picture first'
    : (card.wired ?? 0) > 1
      ? 'One picture at a time — take the extra wires off'
      : !hasBox
        ? 'Drag a box on the picture'
        : !prompt.trim()
          ? 'Say what to change'
          : full
            ? `${live} already going, here or elsewhere — wait for one to finish`
            : ''

  const placeholder = (text: string) => <div className="enode__empty-window">{text}</div>

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'enode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        picture ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      {/* Numbered down the side like the picture card: 1 is the picture being
          edited, the rest are references. */}
      {Array.from({ length: extra + 1 }, (_, index) => {
        const top = ((index + 1) / (extra + 2)) * 100
        const port = index === 0 ? 'in' : `ref-${index}`
        return (
          <Handle
            key={port}
            type="target"
            id={port}
            position={Position.Left}
            className="cnode__port pnode__port"
            style={{ top: `${top}%` }}
          >
            <span className="pnode__portnum">{index + 1}</span>
          </Handle>
        )
      })}
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          {/* The original, all of it, with the box. */}
          <span className="pnode__label">Original · drag a box</span>
          {!picture ? (
            placeholder('Connect a picture')
          ) : !top ? (
            placeholder('Loading the picture…')
          ) : (
            <Window
              source={top}
              frame={null}
              render={drawBox}
              stamp={shown ? `${shown.x}|${shown.y}|${shown.w}|${shown.h}` : '-'}
              onLeft={startBox}
              cursor="crosshair"
            />
          )}

          <textarea
            className="pnode__prompt nodrag nowheel"
            rows={2}
            placeholder="What should be different in the box?"
            value={prompt}
            onChange={(event) => edit({ prompt: event.target.value })}
          />

          {/* Run, between the words and the result (Huanrui, 2026-10-06). */}
          <button
            type="button"
            className="cnode__run pnode__run nodrag"
            onClick={() => {
              setStarting(true)
              card.onRun?.()
            }}
            disabled={starting || Boolean(why)}
            title={why || 'Redraw what is in the box; everything else stays as it is'}
          >
            {starting ? (
              'Starting…'
            ) : (
              <>
                Run
                {card.estimate ? <span className="pnode__credits">about {card.estimate} credits</span> : null}
                {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
              </>
            )}
          </button>

          {/* The part round the box, enlarged: the original, then the result. */}
          <span className="pnode__label">{patch ? 'Result · Eraser shows the original, Restore the new' : 'Result'}</span>
          {!picture ? (
            placeholder('The result appears here')
          ) : !lower ? (
            placeholder('Loading the full picture…')
          ) : (
            <Window
              source={lower}
              frame={near}
              render={source ? drawResult : () => undefined}
              stamp={`${brush.stamp}|${drawnBox ? `${drawnBox.x}|${drawnBox.y}|${drawnBox.w}|${drawnBox.h}` : '-'}`}
              overlay={patch && !busy ? brush.overlay : undefined}
              onLeft={patch && !busy ? brush.startStroke : undefined}
              cursor={patch && !busy ? 'none' : 'default'}
              onHover={patch && !busy ? brush.setHover : undefined}
              sizing={patch && !busy ? brush.sizing : undefined}
            >
              {running ? (
                <div className="enode__loading">
                  <WwLoaderFill label="Editing" size="large" />
                </div>
              ) : !source ? (
                <div className="enode__soon">Loading the full-size picture…</div>
              ) : null}
            </Window>
          )}

          {patch && !busy ? (
            <BrushControls brush={brush}>
              <button
                type="button"
                className="enode__apply"
                disabled={card.applying === true}
                onClick={() => card.onApply?.()}
                title="Put the result together at full size, in place of the picture this card made"
              >
                {card.applying ? 'Applying…' : 'Apply'}
              </button>
            </BrushControls>
          ) : null}

          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {/* Folded, the button is still there while something is going. */}
      {!isOpen && mine > 0 ? (
        <button type="button" className="cnode__run pnode__run nodrag" disabled title="Running">
          Running<span className="pnode__credits">· {mine} going</span>
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
